import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { logger } from "../log";
import type { SyncObserver } from "../sync";
import { ErrorLike } from "../types";
import { notEmpty } from "../utils";
import { getInstance } from "./db";

const log = logger("resource");
const db = getInstance("resources");
const defaultDefaultExpiry = 24 * 60 * 60 * 1000;

export type Resource = {
  readonly url: string;
  readonly data: string;
  readonly createdAt: number;
  readonly expiresAt?: number;
};

export type ResourceState =
  | { readonly state: "loading" }
  | { readonly state: "error"; readonly error: ErrorLike }
  | { readonly state: "ready"; readonly data: string };

export type ResourceParams = {
  readonly cacheKey?: string;
  readonly defaultExpiry?: number;
  readonly overrideExpiry?: number;
  readonly requestOptions?: RequestInit;
};

export function useResource(
  url: string,
  params?: ResourceParams,
): ResourceState {
  const [state, setState] = useState<ResourceState>({ state: "loading" });
  const paramsRef = useRef(params);
  useLayoutEffect(() => {
    paramsRef.current = params;
  });

  const requestIdentity = useMemo(
    () =>
      JSON.stringify([
        params?.cacheKey,
        params?.defaultExpiry,
        params?.overrideExpiry,
        params?.requestOptions,
      ]),
    [
      params?.cacheKey,
      params?.defaultExpiry,
      params?.overrideExpiry,
      params?.requestOptions,
    ],
  );

  useEffect(() => {
    let active = true;
    setState({ state: "loading" });
    getResource(url, paramsRef.current)
      .then((data) => {
        if (active) setState({ state: "ready", data: data.data });
      })
      .catch((error) => {
        if (active) setState({ state: "error", error });
      });

    return () => {
      active = false;
    };
    // requestIdentity captures the semantically relevant fields of `params`;
    // the object itself is not stable across renders.
  }, [url, requestIdentity]);

  return state;
}

export async function getResource(
  url: string,
  params?: ResourceParams,
): Promise<Resource> {
  const cacheKey = params?.cacheKey ?? url;
  const stored = await (await db).readData(cacheKey);

  if (stored) {
    if (isExpired(stored)) {
      // return stale data and update asynchronously
      log.debug(`stale: ${url}`);
      fetchResource(url, params);
    }

    log.debug(`hit: ${url}`);
    return stored;
  }

  log.debug(`miss: ${url}`);
  return fetchResource(url, params);
}

function isExpired(res: Resource) {
  return !res.expiresAt || res.expiresAt < Date.now();
}

async function fetchResource(
  url: string,
  params?: ResourceParams,
): Promise<Resource> {
  const createdAt = Date.now();
  const response = await fetch(url, params?.requestOptions);
  if (!response.ok)
    throw new Error(`${response.status} ${response.statusText}`);

  const expiresAt = getExpiry(response.headers, params);
  log.debug(`store: ${url}, expires at ${new Date(expiresAt).toISOString()}`);

  const resource: Resource = {
    url,
    data: await response.blob().then(blob2base64),
    createdAt,
    expiresAt,
  };

  const cacheKey = params?.cacheKey ?? url;

  try {
    await (await db).writeData(cacheKey, resource);
  } catch (error: unknown) {
    log.error(`Failed to write cache key ${cacheKey}:`, error);
  }

  return resource;
}

function getExpiry(headers: Headers, params?: ResourceParams): number {
  if (params?.overrideExpiry) return Date.now() + params.overrideExpiry;

  const expires = headers.get("expires");
  if (expires) return new Date(expires).getTime();

  const cacheControl = headers.get("cache-control");
  if (cacheControl) {
    const cacheControlExpiry = getCacheControlExpiry(cacheControl);
    if (cacheControlExpiry) return cacheControlExpiry;
  }

  return Date.now() + (params?.defaultExpiry ?? defaultDefaultExpiry);
}

function getCacheControlExpiry(value: string): number | undefined {
  const match = value.match(/max-age=(\d+)/);
  if (match) return Date.now() + parseInt(match[1], 10) * 1000;
}

export type ResourceStatistics = {
  count: number;
};

export async function getResourceStatistics(): Promise<ResourceStatistics> {
  const d = await db;
  const files = await d.listFiles();
  return { count: files.length };
}

async function blob2base64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result as string));
    reader.addEventListener("error", reject);
    reader.readAsDataURL(blob);
  });
}

export async function clearResources() {
  await (await db).clear();
}

export type PrecacheOptions = {
  /**
   * Skip resources that still fail after retrying instead of aborting the
   * whole run — for third-party sources (e.g. WMS services) where single
   * failing tiles are expected. Fails only if nothing could be loaded.
   */
  readonly skipFailures?: boolean;
};

export async function precacheResources(
  urls: ReadonlyArray<string>,
  params?: ResourceParams,
  observer?: SyncObserver,
  options?: PrecacheOptions,
) {
  const expiredUrls = await getExpiredResources(urls);
  observer?.addWork(expiredUrls.length);
  let failed = 0;
  let lastError: unknown;
  for (const url of expiredUrls) {
    try {
      await retryWithBackoff(
        async () => {
          if (observer?.canceled) return;
          await getResource(url, params);
        },
        2,
        1000,
      );
    } catch (error: unknown) {
      if (!options?.skipFailures) throw error;
      failed++;
      lastError = error;
      log.warn(`precache failed: ${url}`, error);
    }
    observer?.workCompleted();
  }

  if (failed > 0) {
    log.warn(`${failed} of ${expiredUrls.length} resources failed to precache`);
    if (failed === expiredUrls.length) throw lastError;
  }
}

async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number,
  initialBackoff: number,
): Promise<T> {
  let remainingTries = maxRetries;
  let backoff = initialBackoff;

  while (true) {
    try {
      return await fn();
    } catch (error: unknown) {
      if (remainingTries <= 0) throw error;
    }

    await new Promise((resolve) => setTimeout(resolve, backoff));
    remainingTries--;
    backoff = backoff * 2;
  }
}

async function getExpiredResources(
  urls: ReadonlyArray<string>,
): Promise<ReadonlyArray<string>> {
  // Checked in chunks: one Promise.all over tens of thousands of storage reads
  // blocks the app (e.g. map tile precaching right after login).
  const chunkSize = 200;
  const database = await db;
  const expired: string[] = [];
  for (let i = 0; i < urls.length; i += chunkSize) {
    const chunk = urls.slice(i, i + chunkSize);
    const results = await Promise.all(
      chunk.map(async (url) => {
        const stored = await database.readData(url);
        return !stored || isExpired(stored) ? url : null;
      }),
    );
    expired.push(...results.filter(notEmpty));
  }
  return expired;
}
